import { module } from 'modujs';

const BEHAVIOUR_CLOSE_ESC_KEY = 'esc';
const BEHAVIOUR_CLOSE_OUTSIDE = 'outside';

const ESC_KEY = 27;

/**
 * Details Popover
 *
 * Adds enhancements to their behavior.
 *
 * Close the disclosure:
 *
 * 1. by clicking outside the component.
 * 2. by clicking a secondary close button.
 * 3. via the Escape key.
 *
 * Based on {@link https://github.com/zachleat/details-utils}.
 *
 * Example:
 *
 * ```html
 * <details data-module-details-popover data-details-popover-close="esc outside"></details>
 * ```
 */
export default class extends module {
    /**
     * @type {?BEHAVIOUR_CLOSE_*[]}
     */
    #closeables;

    /**
     * @return {BEHAVIOUR_CLOSE_*[]}
     */
    get closeables() {
        if (this.#closeables == null) {
            const closeables = this.getData('close');
            if (closeables) {
                this.#closeables = closeables.split(/\s+/);
            } else {
                this.#closeables = [];
            }
        }

        return this.#closeables;
    }

    /**
     * @return {boolean}
     */
    get open() {
        return this.el.open;
    }

    /**
     * @return {?Element}
     */
    get summary() {
        return this.el.querySelector(':scope > summary');
    }

    /**
     * @param {boolean} toggle
     */
    set open(toggle) {
        this.el.open = toggle;
    }

    /**
     * @param {object} options
     */
    constructor(options) {
        super(options);

        this.handleClose = this.handleClose.bind(this);
        this.handleCloseOnEsc = this.handleCloseOnEsc.bind(this);
        this.handleCloseOnOutside = this.handleCloseOnOutside.bind(this);

        this.events = {
            'click': {
                'close': 'handleClose',
            },
        };
    }

    init() {
        if (this.closeables.includes(BEHAVIOUR_CLOSE_ESC_KEY)) {
            document.addEventListener('keydown', this.handleCloseOnEsc, false);
        }

        if (this.closeables.includes(BEHAVIOUR_CLOSE_OUTSIDE)) {
            document.addEventListener('mousedown', this.handleCloseOnOutside, false);
        }
    }

    destroy() {
        document.removeEventListener('keydown', this.handleCloseOnEsc, false);
        document.removeEventListener('mousedown', this.handleCloseOnOutside, false);
    }

    /**
     * @listens click
     *
     * @param {Event} event
     */
    handleClose(event) {
        this.open = false;
        this.summary?.focus();
    }

    /**
     * @listens keydown
     *
     * @param {Event} event
     */
    handleCloseOnEsc(event) {
        if (event.keyCode !== ESC_KEY) {
            return;
        }

        if (!this.open) {
            return;
        }

        this.open = false;
        this.summary?.focus();
    }

    /**
     * @listens mousedown
     *
     * @param {Event} event
     */
    handleCloseOnOutside(event) {
        if (!this.open) {
            return;
        }

        if (isChildOfParent(event.target, this.el)) {
            return;
        }

        this.open = false;
    }
}

/**
 * @param  {Element} target
 * @param  {Element} parent
 * @return {boolean}
 */
function isChildOfParent(target, parent) {
    while (target && target.parentNode) {
        if (target.parentNode === parent) {
            return true;
        }

        target = target.parentNode;
    }

    return false;
}
